import XRegExp from 'xregexp';
import { ParseIntError, safeParseInt } from '../../utils/numbers';
import { normalizeConfusables } from './confusables';
import { getDistance } from './distance';

const PUNCTUATION_REGEX = '[\\p{P}\\p{S}\\p{C}]+';
const STRICT_REGEX = '[\\p{C}]+';
const WHITESPACE_REGEX = '[\\p{White_Space}]+';
const DASH_REGEX = '-+';
const ARABIC_REGEX = '[\u064B-\u065B]+';
// Scripts which don't have space delimiters http://www.unicode.org/standard/supported.html
const IDEOGRAPH_REGEX = '[\\p{Han}\\p{Hiragana}\\p{Katakana}]+';
const SEGMENTATION_REGEX = /(\.{2,}|(?:(?!\d))[!"#$%&*+.,/:;<=>¿?@\\^_`|](?!\d)|\s)/;

const FIRST_LETTER_WEIGHT = 0.1;
const DISTANCE_WEIGHT = 0.9;

const DEFAULT_NORMALIZE_FORM = 'NFKC';

export const getScore = (
  unsanitizedCorrectAnswer: string,
  unsanitizedUserAnswer: string,
  isTapping = false,
  isStrict = false,
  isRomanized = false,
): number => {
  const spaceSanitizedUserAnswer = isRomanized ? sanitizeAnswerForRomanizedLanguage(unsanitizedUserAnswer) : unsanitizedUserAnswer;
  const spaceSanitizedCorrectAnswer = isRomanized ? sanitizeAnswerForRomanizedLanguage(unsanitizedCorrectAnswer) : unsanitizedCorrectAnswer;

  const sanitizedUserAnswer = sanitizeUserAnswer(
    spaceSanitizedUserAnswer,
    isTapping,
    isStrict,
  );

  const sanitizedCorrectAnswer = sanitizeUserAnswer(spaceSanitizedCorrectAnswer, isTapping, isStrict);

  if (isNumeric(sanitizedCorrectAnswer, sanitizedUserAnswer)) {
    return getNumericScore(sanitizedCorrectAnswer, sanitizedUserAnswer);
  }
  return getStringScore(sanitizedCorrectAnswer, sanitizedUserAnswer);
};

const isNumeric = (correctAnswer: string, userAnswer: string): boolean => {
  const userAnswerNotNumeric =
    safeParseInt(userAnswer) instanceof ParseIntError;
  const correctAnswerNotNumeric =
    safeParseInt(correctAnswer) instanceof ParseIntError;

  return !(userAnswerNotNumeric && correctAnswerNotNumeric);
};

const getNumericScore = (
  userAnswer: string,
  correctAnswer: string,
): number => {
  /* Don't fuzzy match on numbers, compare exactly */
  const userAnswerNumeric = safeParseInt(userAnswer);
  const correctAnswerNumeric = safeParseInt(correctAnswer);

  if (
    userAnswerNumeric instanceof ParseIntError ||
    correctAnswerNumeric instanceof ParseIntError
  ) {
    return 0;
  }

  return userAnswerNumeric === correctAnswerNumeric ? 1.0 : 0.0;
};

const getStringScore = (
  correctAnswer: string,
  userAnswer: string,
): number => {
  const tolerance = getToleranceFromLength(correctAnswer.length);
  const distance = getDistance(userAnswer, correctAnswer);

  if (distance >= tolerance) return 0;

  const firstLetterScore = doFirstLettersMatch(userAnswer, correctAnswer)
    ? 1
    : /* istanbul ignore next: untested branch of code, please test */ 0;
  const distanceScore = (tolerance - distance) / tolerance;

  const score =
    FIRST_LETTER_WEIGHT * firstLetterScore +
    DISTANCE_WEIGHT * distanceScore;

  // Ignore low scores
  if (score < 0.5) return 0;

  return score;
};

const doFirstLettersMatch = (
  userAnswer: string,
  correctAnswer: string,
): boolean => userAnswer.charAt(0) === correctAnswer.charAt(0);

const getToleranceFromLength = (answerLength: number): number => {
  if (answerLength > 18) {
    return 0.5 * answerLength;
  }
  if (answerLength < 3) {
    return 1.0;
  }

  // Please note - this has been copied from webapp logic but
  // doesn't seem to have an explanation why these values are used.
  return ((answerLength * -1) / 33.0 + 1.1) * answerLength;
};

export const getWordSanitizer = (
  isTapping: boolean,
  isStrict: boolean,
) => {
  if (isTapping) {
    return sanitizeWordTapping;
  }
  // The backend says that it doesn't matter if a Tapping test is strict
  // however it also sends isStrict on TappingTests, so we handle the
  // case when isStrict is passed for TappingTests
  // If you want to use strict sanitization with TappingTests
  // the order of these checks can be reversed
  if (isStrict) {
    return sanitizeWordStrict;
  }
  return sanitizeWord;
};

export const sanitizeAnswerForRomanizedLanguage = (unsanitizedAnswer: string) => {
  let answer = unsanitizedAnswer;
  answer  = replaceCharacters(answer, WHITESPACE_REGEX);
  answer = replaceCharacters(answer, DASH_REGEX);
  return answer;
};

export const sanitizeUserAnswer = (
  unsanitizedAnswer: string,
  isTapping = false,
  isStrict = false,
): string => {
  const delimiter = getDelimiter(unsanitizedAnswer);
  let words = segment(unsanitizedAnswer, delimiter);

  // Tapping tests have placeholders (...) which need to be treated differently
  words = isTapping ? removePlaceholdersBeginningAndEnd(words) : words;
  const sanitize = getWordSanitizer(isTapping, isStrict);

  words = words.map((word: string): string => sanitize(word));
  words = filterForEmpty(words);

  return words.join(delimiter);
};

export const sanitizeWord = (unsanitizedAnswer: string): string => {
  let answer = unsanitizedAnswer;

  answer = replaceCharacters(answer, '[()]+/');
  answer = replaceCharacters(answer, PUNCTUATION_REGEX);
  answer = replaceCharacters(answer, WHITESPACE_REGEX, ' ');
  answer = replaceCharacters(answer, ARABIC_REGEX);
  answer = answer.normalize(DEFAULT_NORMALIZE_FORM);

  // Fold visually-confusable characters before case-folding: some map keys are
  // uppercase (e.g. Greek Α→A), so lowercasing first would lose the match.
  answer = normalizeConfusables(answer);

  answer = answer.toLowerCase();
  answer = answer.trim();

  return answer;
};

export const sanitizeWordStrict = (unsanitizedAnswer: string): string => {
  let answer = unsanitizedAnswer;

  answer = replaceCharacters(answer, STRICT_REGEX);

  answer = answer.normalize(DEFAULT_NORMALIZE_FORM);

  // Fold visually-confusable characters. Strict mode preserves case, so this
  // must run on the NFKC-normalised (still cased) string.
  answer = normalizeConfusables(answer);

  return answer;
};

export const isTappingTestPlaceholder = (
  unsanitizedAnswer: string,
): boolean => {
  const pattern = XRegExp(/^\.{2,}$/);
  return XRegExp.test(unsanitizedAnswer, pattern);
};

export const removePlaceholdersBeginningAndEnd = (
  words: string[],
): string[] => {
  let filteredWords = [...words];

  // Find index of first non placeholder and slice
  let i = 0;
  while (
    i < filteredWords.length &&
    isTappingTestPlaceholder(filteredWords[i])
  )
    i += 1;
  filteredWords = filteredWords.slice(i, filteredWords.length);

  // Find index of last non placeholder and slice
  i = filteredWords.length - 1;
  while (i > 0 && isTappingTestPlaceholder(filteredWords[i])) i -= 1;
  filteredWords = filteredWords.slice(0, i + 1);

  return filteredWords;
};

export const sanitizeWordTapping = (unsanitizedAnswer: string): string => {
  // Don't sanitize user answer of '...'
  if (isTappingTestPlaceholder(unsanitizedAnswer))
    return unsanitizedAnswer;

  return sanitizeWord(unsanitizedAnswer);
};

export const replaceCharacters = (
  answer: string,
  regex: string,
  newValue = '',
): string => {
  const pattern = XRegExp(regex);
  return XRegExp.replace(answer, pattern, newValue, 'all');
};

export const segment = (
  unsanitizedAnswer: string,
  delimiter: string,
): string[] => {
  // Standardize the string before splitting it
  const trimmedAnswer = unsanitizedAnswer.trim();
  let sanitizedAnswer = XRegExp.replace(
    trimmedAnswer,
    XRegExp('[\\p{White_Space}]+'),
    ' ',
    'all',
  );

  let segments;
  if (delimiter === ' ') {
    segments = sanitizedAnswer.split(SEGMENTATION_REGEX);
  } else {
    // This wont currently work for Pinyin as the characters are romanized
    sanitizedAnswer = replaceCharacters(sanitizedAnswer, WHITESPACE_REGEX);
    sanitizedAnswer = replaceCharacters(sanitizedAnswer, DASH_REGEX);

    segments = sanitizedAnswer.split(delimiter);
  }

  return filterForEmpty(segments);
};

const filterForEmpty = (segments: string[]): string[] =>
  segments.filter(segment => segment.trim() !== '');

const getDelimiter = (answer: string): string =>
  XRegExp(IDEOGRAPH_REGEX).test(answer) ? '' : ' ';
